import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FileSpreadsheet, FileText, Calendar, ArrowLeft, Download,
  CheckCircle, XCircle, AlertTriangle, DollarSign, CreditCard, Clock
} from 'lucide-react';
import { reportsAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { toast } from 'react-hot-toast';
import ListPageHeader from '../../components/shared/ListPageHeader';
import ColumnFilter from '../../components/shared/ColumnFilter';

const ITCEligibilityPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [eligibilityData, setEligibilityData] = useState(null);

  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    supplier_gstin: '',
    supplier_name: '',
    invoice_no: '',
    eligibility: []
  });

  useEffect(() => {
    if (businessId) {
      fetchITCEligibility();
    }
  }, [month, year, businessId]);

  const fetchITCEligibility = async () => {
    try {
      setLoading(true);
      const response = await reportsAPI.itcEligibility({ month, year });
      setEligibilityData(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      const response = await reportsAPI.exportReport({ report_type: 'itc-eligibility', format, month, year });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ITC_Eligibility_${month.toString().padStart(2, '0')}_${year}.${format === 'pdf' ? 'pdf' : format === 'excel' ? 'xlsx' : 'json'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const formatCompactCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  const summary = eligibilityData?.summary || {
    total_itc: 0,
    eligible: 0,
    ineligible: 0,
    blocked: 0,
    reversal_required: 0
  };

  const invoices = eligibilityData?.invoices || [];

  // Apply filters
  const filteredInvoices = invoices.filter(inv => {
    const matchesGstin = !columnFilters.supplier_gstin || 
      inv.supplier_gstin?.toLowerCase().includes(columnFilters.supplier_gstin.toLowerCase());
    const matchesName = !columnFilters.supplier_name || 
      inv.supplier_name?.toLowerCase().includes(columnFilters.supplier_name.toLowerCase());
    const matchesInvoice = !columnFilters.invoice_no || 
      inv.invoice_no?.toLowerCase().includes(columnFilters.invoice_no.toLowerCase());
    const matchesEligibility = columnFilters.eligibility.length === 0 || 
      columnFilters.eligibility.includes(inv.eligibility);
    return matchesGstin && matchesName && matchesInvoice && matchesEligibility;
  });

  const months = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2000, i).toLocaleString('default', { month: 'long' })
  }));

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const monthLabel = new Date(year, month - 1).toLocaleString('default', { month: 'short', year: 'numeric' });

  const getEligibilityBadge = (eligibility) => {
    const badges = {
      eligible: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: CheckCircle, label: 'Eligible' },
      ineligible: { bg: 'bg-rose-50', text: 'text-rose-700', icon: XCircle, label: 'Ineligible' },
      blocked: { bg: 'bg-slate-100', text: 'text-slate-700', icon: AlertTriangle, label: 'Blocked' },
      reversal: { bg: 'bg-amber-50', text: 'text-amber-700', icon: Clock, label: 'Reversal Req.' }
    };
    return badges[eligibility] || badges.ineligible;
  };

  return (
    <div className="h-full flex flex-col" data-testid="itc-eligibility-page">
      {/* Premium Header */}
      <ListPageHeader
        title="ITC Eligibility Check"
        stats={[
          { icon: DollarSign, label: 'Total ITC', value: formatCompactCurrency(summary.total_itc), color: 'slate' },
          { icon: CheckCircle, label: 'Eligible', value: formatCompactCurrency(summary.eligible), color: 'emerald' },
          { icon: XCircle, label: 'Ineligible', value: formatCompactCurrency(summary.ineligible), color: 'rose' },
          { icon: AlertTriangle, label: 'Blocked', value: formatCompactCurrency(summary.blocked), color: 'amber' },
        ]}
        secondaryAction={{
          label: monthLabel,
          icon: Calendar,
          onClick: () => {}
        }}
      />

      {/* Controls Bar */}
      <div className="flex-shrink-0 px-2 pt-2">
        <div className="bg-white rounded-md border border-slate-200/60 shadow-sm p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/business/${businessId}/reports`)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors text-[12px] font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              
              <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5">
                <Calendar className="w-4 h-4 text-slate-400" />
                <select 
                  value={month} 
                  onChange={(e) => setMonth(parseInt(e.target.value))} 
                  className="text-[12px] text-slate-600 bg-transparent border-none focus:outline-none cursor-pointer"
                >
                  {months.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <select 
                  value={year} 
                  onChange={(e) => setYear(parseInt(e.target.value))} 
                  className="text-[12px] text-slate-600 bg-transparent border-none focus:outline-none cursor-pointer"
                >
                  {years.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button onClick={() => handleExport('excel')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50">
                <FileSpreadsheet className="w-3.5 h-3.5" />
                Excel
              </button>
              <button onClick={() => handleExport('pdf')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-rose-600 rounded-lg hover:bg-rose-700 transition-colors disabled:opacity-50">
                <FileText className="w-3.5 h-3.5" />
                PDF
              </button>
              <button onClick={() => handleExport('json')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
                <Download className="w-3.5 h-3.5" />
                JSON
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table Area */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading ITC eligibility...</p>
              </div>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <CreditCard className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">No ITC eligibility data</p>
              <p className="text-[13px] text-slate-400 mt-1">Purchase ITC eligibility analysis will appear here</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Table Header */}
              <div className="flex-shrink-0">
                <table className="tl-table">
                  <thead>
                    <tr>
                      <th className="tl-th tl-th-teal" style={{ width: '14%' }}>
                        <div className="flex items-center gap-1">
                          Supplier GSTIN
                          <ColumnFilter 
                            type="text" 
                            value={columnFilters.supplier_gstin}
                            onChange={(val) => setColumnFilters({...columnFilters, supplier_gstin: val})}
                            placeholder="Filter..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '16%' }}>
                        <div className="flex items-center gap-1">
                          Supplier Name
                          <ColumnFilter 
                            type="text" 
                            value={columnFilters.supplier_name}
                            onChange={(val) => setColumnFilters({...columnFilters, supplier_name: val})}
                            placeholder="Filter..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '12%' }}>
                        <div className="flex items-center gap-1">
                          Invoice No
                          <ColumnFilter 
                            type="text" 
                            value={columnFilters.invoice_no}
                            onChange={(val) => setColumnFilters({...columnFilters, invoice_no: val})}
                            placeholder="Filter..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '10%' }}>Date</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '12%' }}>Total ITC (₹)</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '12%' }}>Eligible (₹)</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '10%' }}>Blocked (₹)</th>
                      <th className="tl-th tl-th-teal" style={{ width: '14%' }}>
                        <div className="flex items-center gap-1">
                          Eligibility
                          <ColumnFilter 
                            type="status" 
                            value={columnFilters.eligibility}
                            onChange={(val) => setColumnFilters({...columnFilters, eligibility: val})}
                            options={[
                              { value: 'eligible', label: 'Eligible' },
                              { value: 'ineligible', label: 'Ineligible' },
                              { value: 'blocked', label: 'Blocked' },
                              { value: 'reversal', label: 'Reversal Req.' }
                            ]}
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>
                </table>
              </div>

              {/* Table Body */}
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table">
                  <tbody className="animate-stagger">
                    {filteredInvoices.map((inv, idx) => {
                      const eligibilityBadge = getEligibilityBadge(inv.eligibility);
                      const EligibilityIcon = eligibilityBadge.icon;
                      return (
                        <tr 
                          key={idx} 
                          className={`tl-tr hover:bg-teal-50/40 animate-tableRowFadeIn ${idx % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                          style={{ animationDelay: `${Math.min(idx * 30, 300)}ms` }}
                        >
                          <td className="tl-td" style={{ width: '14%' }}>
                            <span className="text-[11px] font-mono text-slate-600">{inv.supplier_gstin}</span>
                          </td>
                          <td className="tl-td tl-cell-regular" style={{ width: '16%' }}>{inv.supplier_name}</td>
                          <td className="tl-td tl-cell-primary" style={{ width: '12%' }}>{inv.invoice_no}</td>
                          <td className="tl-td tl-cell-regular" style={{ width: '10%' }}>
                            {inv.date ? new Date(inv.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                          </td>
                          <td className="tl-td text-right tl-cell-regular" style={{ width: '12%' }}>₹{(inv.total_itc || 0).toLocaleString()}</td>
                          <td className="tl-td text-right text-[13px] font-semibold text-emerald-600" style={{ width: '12%' }}>₹{(inv.eligible_itc || 0).toLocaleString()}</td>
                          <td className="tl-td text-right text-[13px] font-medium text-amber-600" style={{ width: '10%' }}>
                            {inv.blocked_itc ? `₹${inv.blocked_itc.toLocaleString()}` : '—'}
                          </td>
                          <td className="tl-td" style={{ width: '14%' }}>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 ${eligibilityBadge.bg} ${eligibilityBadge.text} rounded-full text-[10px] font-medium`}>
                              <EligibilityIcon className="w-3 h-3" />
                              {eligibilityBadge.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>
                  Showing <span className="font-medium text-slate-700">{filteredInvoices.length}</span> of <span className="font-medium text-slate-700">{invoices.length}</span> invoices
                </p>
                <div className="flex items-center gap-4">
                  <span>Eligible: <span className="font-medium text-emerald-600">₹{summary.eligible.toLocaleString()}</span></span>
                  <span>Blocked: <span className="font-medium text-amber-600">₹{summary.blocked.toLocaleString()}</span></span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ITCEligibilityPage;
